import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Send, Users, MessageSquare, Play, Pause, Edit, Calendar, BarChart3, CheckCircle2, Clock, TrendingUp, AlertCircle, Trash2, Check, Smile, Paperclip, X, XCircle, DollarSign, Copy, ChevronDown, ChevronRight, Settings, Download } from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import Layout from '../components/Layout';
import { campaignsAPI, cobrancaAPI, fluxosAPI } from '../services/api';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../hooks/useConfirm';
import { formatarDataHora } from '../utils/dateFormatter';
import FluxoMiniMap from '../components/FluxoMiniMap';
import { PageSkeleton, SkeletonCard } from '../components/Skeleton';

const CampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const showConfirm = useConfirm();
  const [campaign, setCampaign] = useState(null);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ sent: 0, delivered: 0, read: 0, executions: [] });
  const [conversao, setConversao] = useState(null);
  const [pendentes, setPendentes] = useState(null);
  // Fila do pool: só existe quando a campanha foi materializada (persona).
  const [fila, setFila] = useState(null);
  const [reenfileirando, setReenfileirando] = useState(false);
  const [grupoParticipantes, setGrupoParticipantes] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsTotal, setRecipientsTotal] = useState(0);
  const [recipientsPages, setRecipientsPages] = useState(1);
  const [recipientsSearch, setRecipientsSearch] = useState('');
  const [recipientsStatus, setRecipientsStatus] = useState('');
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reenviando, setReenviando] = useState(false);
  const [activeTab, setActiveTab] = useState('messages');
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [savingMessage, setSavingMessage] = useState(false);
  const [newMessageText, setNewMessageText] = useState('');
  const [addingMessage, setAddingMessage] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showNewEmojiPicker, setShowNewEmojiPicker] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [variaveisCampanha, setVariaveisCampanha] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const emojiPickerRef = useRef(null);
  const newEmojiPickerRef = useRef(null);
  const chatEndRef = useRef(null);
  const creatingTemplateMsg = useRef(false);
  const { darkMode } = useTheme();
  const [custoEstimado, setCustoEstimado] = useState(null);
  const [custoReal, setCustoReal] = useState(null);
  const [fluxoData, setFluxoData] = useState(null);

  useEffect(() => {
    loadCampaignDetails();
  }, [id]);

  // Polling: atualiza stats a cada 2s enquanto campanha está executando
  useEffect(() => {
    if (campaign?.status_execucao !== 'executando') return;
    const interval = setInterval(async () => {
      try {
        const [statsRes, campRes] = await Promise.all([
          campaignsAPI.getStats(id),
          campaignsAPI.get(id),
        ]);
        if (statsRes.data) setStats(statsRes.data);
        if (campRes.data) {
          setCampaign(campRes.data);
          if (campRes.data.status_execucao !== 'executando') clearInterval(interval);
        }
      } catch (e) {}
    }, 2000);
    return () => clearInterval(interval);
  }, [campaign?.status_execucao, id]);

  // Polling lento: detecta quando campanha agendada começa a executar
  useEffect(() => {
    if (campaign?.status_execucao && campaign.status_execucao !== 'null') return;
    if (!campaign?.data_agendamento) return;
    const interval = setInterval(async () => {
      try {
        const campRes = await campaignsAPI.get(id);
        if (campRes.data && campRes.data.status_execucao === 'executando') {
          setCampaign(campRes.data);
          clearInterval(interval);
        }
      } catch (e) {}
    }, 10000);
    return () => clearInterval(interval);
  }, [campaign?.status_execucao, campaign?.data_agendamento, id]);

  const loadRecipients = async (page = 1, search = '', status = '') => {
    if (!id) return;
    setRecipientsLoading(true);
    try {
      const res = await campaignsAPI.getRecipients(id, { page, limit: 50, search, status });
      const data = res.data;
      setRecipients(data.data || []);
      setRecipientsTotal(data.total || 0);
      setRecipientsPages(data.pages || 1);
      setRecipientsPage(page);
    } catch (e) {
      console.error('Erro ao carregar recipients:', e);
    } finally {
      setRecipientsLoading(false);
    }
  };

  const [exportingCsv, setExportingCsv] = useState(false);
  const handleExportCsv = async () => {
    if (!id) return;
    setExportingCsv(true);
    try {
      // Exporta respeitando os filtros ativos (busca + status), igual à tabela.
      const res = await campaignsAPI.exportRecipients(id, {
        search: recipientsSearch,
        status: recipientsStatus,
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const nomeBase = (campaign?.nome || 'campanha').replace(/[^a-zA-Z0-9-_]+/g, '_');
      link.download = `relatorio-${nomeBase}${recipientsStatus ? '-' + recipientsStatus : ''}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório exportado');
    } catch (e) {
      console.error('Erro ao exportar CSV:', e);
      toast.error('Não foi possível exportar o relatório');
    } finally {
      setExportingCsv(false);
    }
  };

  useEffect(() => {
    if (campaign && campaign.status_execucao) {
      loadRecipients(1, '');
    }
  }, [campaign?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
      if (newEmojiPickerRef.current && !newEmojiPickerRef.current.contains(event.target)) {
        setShowNewEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, newMessageText]);

  const loadCampaignDetails = async () => {
    try {
      const campaignResponse = await campaignsAPI.get(id);
      const campaignData = campaignResponse.data;
      setCampaign(campaignData);
      setVariaveisCampanha(campaignData.variaveis_campanha || {});

      const messagesResponse = await campaignsAPI.getMessages(id);
      const messagesData = messagesResponse.data || [];
      setMessages(messagesData);

      // Carregar URLs de mídia
      for (const msg of messagesData) {
        if (msg.media_id && msg.media?.nome?.startsWith('s3://')) {
          try {
            const res = await campaignsAPI.getMediaUrl(msg.media_id);
            if (res.data?.url) msg.media._url = res.data.url;
          } catch {}
        }
      }
      if (messagesData.some(m => m.media?._url)) setMessages([...messagesData]);

      // Se tem template mas não tem mensagens, criar mensagem do template (apenas uma vez)
      if (campaignData.template_id && campaignData.template && messagesData.length === 0 && !creatingTemplateMsg.current) {
        creatingTemplateMsg.current = true;
        try {
          await campaignsAPI.addMessage(id, {
            text: campaignData.template.mensagem,
            ordem: 1,
            usuario_id: campaignData.usuario_id,
            campanha_id: id,
          });
          // Recarregar mensagens após criar
          const newMessagesResponse = await campaignsAPI.getMessages(id);
          setMessages(newMessagesResponse.data || []);
        } catch (error) {
          console.error('Erro ao criar mensagem do template:', error);
        }
      }

      const statsResponse = await campaignsAPI.getStats(id);
      const statsData = statsResponse.data || { sent: 0, delivered: 0, read: 0, executions: [] };
      setStats(statsData);

      // Carregar taxa de conversão (não se aplica a campanhas de grupo)
      if (campaignData.tipo_campanha !== 'grupo_whatsapp') {
        try {
          const conversaoRes = await api.get(`/campanhas/${id}/conversao`);
          if (conversaoRes.data) setConversao(conversaoRes.data);
        } catch (e) {}
      }

      // Buscar custo estimado ou real (apenas para campanhas individuais — Z-API não cobra)
      try {
        if (campaignData.tipo_campanha !== 'grupo_whatsapp') {
          if (['completa', 'incompleta'].includes(campaignData.status_execucao)) {
            const custoRes = await campaignsAPI.getCusto(id);
            if (custoRes.data) setCustoReal(custoRes.data);
          } else {
            const totalContatos = campaignData.total_recipients || campaignData.total_contatos || statsData.sent || 0;
            const custoRes = await cobrancaAPI.calcular(
              user.cliente?.id || user.cliente_id,
              totalContatos,
              campaignData.template?.categoria || 'marketing',
            );
            if (custoRes.data) setCustoEstimado(custoRes.data);
          }
        }
      } catch (e) { }

      // Carregar contatos pendentes se a campanha foi executada e está incompleta
      // Fila do pool. Silencioso quando não há: campanha de número único não
      // materializa fila, e o painel simplesmente não aparece.
      try {
        const { data: filaData } = await campaignsAPI.getFila(id);
        if (filaData?.usando_pool) setFila(filaData);
      } catch (e) {
        // Endpoint indisponível não pode derrubar a tela de detalhe.
      }

      if (campaignData.status_execucao === 'incompleta' || statsData.sent > 0) {
        try {
          const pendentesResponse = await campaignsAPI.getPendentes(id);
          const pendentesData = pendentesResponse.data;

          // Só mostrar aba pendentes se realmente há contatos que não receberam
          if (pendentesData.pendentes > 0) {
            setPendentes(pendentesData);
          }
        } catch (error) {
          console.error('Erro ao carregar pendentes:', error);
        }
      }

      // Buscar participantes dos grupos WhatsApp
      if (campaignData.tipo_origem === 'grupo_whatsapp') {
        try {
          const res = await campaignsAPI.getRecipients(id);
          if (res.data) setGrupoParticipantes(res.data?.data || res.data);
        } catch (e) { }
      }

      // Carregar fluxo se campanha usa um
      if (campaignData.fluxo_id) {
        try {
          const fluxoRes = await fluxosAPI.get(campaignData.fluxo_id);
          if (fluxoRes.data?.data) setFluxoData(fluxoRes.data.data);
          else if (fluxoRes.data?.etapas) setFluxoData(fluxoRes.data);
        } catch (e) { }
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes da campanha');
      navigate('/marketing/campanhas');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (messages.length === 0 && !campaign?.template_id) {
      toast.error('Adicione pelo menos uma mensagem antes de executar a campanha');
      setActiveTab('messages');
      return;
    }
    setShowExecuteModal(true);
  };

  const confirmarExecucao = async () => {
    // Validar variáveis obrigatórias
    const texto = campaign?.template?.mensagem || messages?.[0]?.text || '';
    const allVars = [...new Set((texto.match(/\{\{(\w+)\}\}/g) || []).map(v => v.replace(/\{\{|\}\}/g, '')))];
    const customVars = allVars.filter(v => !['nome', 'telefone'].includes(v.toLowerCase()));
    const vazias = customVars.filter(v => !variaveisCampanha[v]?.trim());
    if (vazias.length > 0) {
      toast.error(`Preencha as variáveis antes de executar: ${vazias.join(', ')}`);
      setShowExecuteModal(false);
      return;
    }

    setShowExecuteModal(false);
    setExecuting(true);
    try {
      // Salvar variáveis automaticamente
      if (customVars.length > 0) {
        await campaignsAPI.update(id, { variaveis_campanha: variaveisCampanha });
      }
      const res = await campaignsAPI.execute(id);
      if (res.data?.success === false) {
        toast.error(res.data.message || 'Erro ao executar campanha');
      } else {
        toast.success(`Campanha iniciada: ${res.data.total_contatos || ''} contatos em fila`);
      }
      loadCampaignDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao executar campanha');
    } finally {
      setExecuting(false);
    }
  };

  const [templateCategoria, setTemplateCategoria] = useState('MARKETING');

  const handleSubmeterTemplate = async () => {
    if (messages.length === 0) {
      toast.error('Adicione pelo menos uma mensagem antes de submeter');
      setActiveTab('messages');
      return;
    }

    setShowSubmitModal(true);
  };

  const confirmarSubmissao = async () => {
    setShowSubmitModal(false);
    setSubmitting(true);
    try {
      const response = await campaignsAPI.submeterTemplate(id, { categoria: templateCategoria });
      toast.success(response.data.message);
      loadCampaignDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao submeter template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerificarStatus = async () => {
    try {
      const response = await campaignsAPI.verificarStatusTemplate(id);
      const status = response.data.status;

      if (status === 'APPROVED') {
        toast.success('✅ Template aprovado! Você pode iniciar a campanha.');
      } else if (status === 'REJECTED') {
        toast.error(`❌ Template rejeitado: ${response.data.rejected_reason}`);
      } else if (status === 'PENDING') {
        toast.info('⏳ Template ainda em análise. Aguarde...');
      }

      loadCampaignDetails();
    } catch (error) {
      toast.error('Erro ao verificar status');
    }
  };

  const handleToggleStatus = async () => {
    try {
      await campaignsAPI.update(id, { status: !campaign.status });
      toast.success(`Campanha ${campaign.status ? 'pausada' : 'ativada'}`);
      setCampaign({ ...campaign, status: !campaign.status });
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleReenviarPendentes = async () => {
    if (!pendentes || pendentes.pendentes === 0) {
      toast.info('Não há contatos pendentes para reenviar');
      return;
    }

    showConfirm({
      title: 'Reenviar Campanha',
      message: `Reenviar campanha para ${pendentes.pendentes} contato(s) pendente(s)?`,
      confirmText: 'Reenviar',
      type: 'warning',
      onConfirm: async () => {
        setReenviando(true);
        try {
          const response = await campaignsAPI.reenviarPendentes(id);
          if (response.data.success) {
            toast.success(response.data.message);
            setTimeout(() => {
              loadCampaignDetails();
            }, 2000);
          } else {
            toast.error(response.data.message);
          }
        } catch (error) {
          toast.error('Erro ao reenviar para contatos pendentes');
        } finally {
          setReenviando(false);
        }
      }
    });
  };

  const handleEditMessage = (message) => {
    setEditingMessage(message.id);
    setEditText(message.text);
    // Ajustar altura do textarea após renderizar
    setTimeout(() => {
      const textarea = document.querySelector('textarea[autofocus]');
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      }
    }, 0);
  };

  const handleSaveMessage = async (messageId) => {
    if (!editText.trim()) {
      toast.error('Mensagem não pode estar vazia');
      return;
    }

    setSavingMessage(true);
    try {
      await campaignsAPI.updateMessage(id, messageId, { text: editText });
      toast.success('Mensagem atualizada!');
      setEditingMessage(null);
      loadCampaignDetails();
    } catch (error) {
      toast.error('Erro ao atualizar mensagem');
    } finally {
      setSavingMessage(false);
    }
  };

  const handleMediaSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamanho (max 100MB - limite da Z-API)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 100MB');
      return;
    }

    setMediaFile(file);

    // Preview
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setMediaPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setMediaPreview(null);
    }
  };

  const handleRemoveMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditText('');
  };

  const handleDeleteMessage = async (messageId) => {
    setMessageToDelete(messageId);
    setShowDeleteModal(true);
  };

  const confirmDeleteMessage = async () => {
    try {
      await campaignsAPI.removeMessage(id, messageToDelete);
      toast.success('Mensagem excluída!');
      setShowDeleteModal(false);
      setMessageToDelete(null);
      loadCampaignDetails();
    } catch (error) {
      toast.error('Erro ao excluir mensagem');
    }
  };

  const handleAddMessage = async () => {
    if (!newMessageText.trim() && !mediaFile) {
      toast.error('Digite uma mensagem ou anexe uma mídia');
      return;
    }

    // Bloquear se já tem fluxo manual e já tem 1 mensagem
    if (campaign.fluxo_id && messages.length >= 1) {
      toast.error('Esta campanha usa um fluxo personalizado. Não é possível adicionar mais mensagens.');
      return;
    }

    setAddingMessage(true);
    try {
      let mediaId = null;

      // Upload de mídia se houver
      if (mediaFile) {
        setUploadingMedia(true);
        const formData = new FormData();
        formData.append('file', mediaFile);

        const mediaResponse = await campaignsAPI.uploadMedia(user?.id, formData);
        mediaId = mediaResponse.data.id;
        setUploadingMedia(false);
      }

      await campaignsAPI.addMessage(id, {
        text: newMessageText || '',
        ordem: messages.length + 1,
        usuario_id: user?.id,
        media_id: mediaId,
      });

      toast.success('Mensagem adicionada!');
      setNewMessageText('');
      handleRemoveMedia();
      setShowNewEmojiPicker(false);
      loadCampaignDetails();
    } catch (error) {
      toast.error('Erro ao adicionar mensagem');
    } finally {
      setAddingMessage(false);
      setUploadingMedia(false);
    }
  };

  const onEmojiClick = (emojiData) => {
    setEditText(prev => prev + emojiData.emoji);
  };

  const onNewEmojiClick = (emojiData) => {
    setNewMessageText(prev => prev + emojiData.emoji);
  };
  if (loading) {
    return (
      <Layout>
        <PageSkeleton>
          <SkeletonCard linhas={6} />
        </PageSkeleton>
      </Layout>
    );
  }

  if (!campaign) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Campanha não encontrada
          </h3>
        </div>
      </Layout>
    );
  }

  const getStatusBadge = () => {
    if (!campaign.status) {
      return { text: 'Inativa', color: darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700', icon: Pause };
    }
    if (campaign.status_execucao === 'executando') {
      return { text: 'Executando', color: darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700', icon: Play };
    }
    if (campaign.status_execucao === 'pausada') {
      return { text: 'Pausada', color: darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700', icon: Pause };
    }
    if (campaign.status_execucao === 'cancelada') {
      return { text: 'Cancelada', color: darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700', icon: XCircle };
    }
    if (campaign.status_execucao === 'completa') {
      return { text: 'Completa', color: darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700', icon: CheckCircle2 };
    }
    if (campaign.status_execucao === 'incompleta') {
      return { text: 'Incompleta', color: darkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-700', icon: AlertCircle };
    }
    if (stats.sent > 0) {
      return { text: 'Executada', color: darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700', icon: CheckCircle2 };
    }
    if (campaign.scheduled_date) {
      return { text: 'Agendada', color: darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700', icon: Clock };
    }
    return { text: 'Ativa', color: darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700', icon: CheckCircle2 };
  };

  const getTemplateStatusBadge = () => {
    const status = campaign.template_status;
    if (!status || status === 'rascunho') {
      return { text: 'Rascunho', color: 'bg-gray-500 text-white', icon: Edit };
    }
    if (status === 'submetido') {
      return { text: 'Aguardando Aprovação', color: 'bg-yellow-500 text-white', icon: Clock };
    }
    if (status === 'aprovado') {
      return { text: 'Aprovado', color: 'bg-green-500 text-white', icon: CheckCircle2 };
    }
    if (status === 'rejeitado') {
      return { text: 'Rejeitado', color: 'bg-red-500 text-white', icon: AlertCircle };
    }
    if (status === 'disparando') {
      return { text: 'Disparando', color: 'bg-blue-500 text-white', icon: Send };
    }
    if (status === 'concluido') {
      return { text: 'Concluído', color: 'bg-green-600 text-white', icon: CheckCircle2 };
    }
    return { text: status, color: 'bg-gray-500 text-white', icon: AlertCircle };
  };

  const statusBadge = getStatusBadge();
  const templateStatusBadge = getTemplateStatusBadge();
  const StatusIcon = statusBadge.icon;
  const TemplateStatusIcon = templateStatusBadge.icon;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header Melhorado */}
        <div className={`rounded-lg shadow-sm border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="p-6">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
              <div className="flex items-start space-x-4 flex-1">
                <button
                  onClick={() => navigate('/marketing/campanhas')}
                  className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-zinc-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {campaign.nome}
                    </h1>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusBadge.text}
                    </span>
                  </div>
                  {campaign.descricao && (
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
                      {campaign.descricao}
                    </p>
                  )}
                  <div className="flex items-center flex-wrap gap-2 text-sm">
                    <span className={`flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <Calendar className="h-4 w-4 mr-1" />
                      Criada {formatarDataHora(campaign.create_at)}
                    </span>
                    {campaign.scheduled_date && (
                      <span className={`flex items-center ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        <Clock className="h-4 w-4 mr-1" />
                        Agendada para {formatarDataHora(campaign.scheduled_date)}
                      </span>
                    )}
                    {/* Status do Template */}
                    {campaign.template_meta_id && campaign.template_status && campaign.template_status !== 'aprovado' && (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${templateStatusBadge.color}`}>
                        <TemplateStatusIcon className="h-3 w-3 mr-1" />
                        WhatsApp: {templateStatusBadge.text}
                      </span>
                    )}
                    {/* Indicador de template aprovado sendo usado */}
                    {campaign.template_id && campaign.template && (!campaign.template_status || campaign.template_status === 'aprovado') && (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'
                        }`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {campaign.template.nome.replace(/^Template:\s*/i, '')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center flex-wrap gap-2 ml-4">
                {/* Botões de Template - Apenas para campanhas que criam template próprio (não usam template existente) */}
                {campaign.template_meta_id && !campaign.template_id && ['rascunho', 'rejeitado'].includes(campaign.template_status) && (
                  <button
                    onClick={handleSubmeterTemplate}
                    disabled={submitting}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Enviando para Meta...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        {campaign.template_status === 'rejeitado' ? 'Resubmeter Template' : 'Submeter para Aprovação'}
                      </>
                    )}
                  </button>
                )}
                {campaign.template_meta_id && !campaign.template_id && campaign.template_status === 'submetido' && (
                  <button
                    onClick={handleVerificarStatus}
                    className="inline-flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Verificar Status
                  </button>
                )}
                {campaign.template_meta_id && !campaign.template_id && campaign.template_status === 'rejeitado' && campaign.template_rejection_reason && (
                  <div className="text-xs text-red-500 max-w-xs">
                    Motivo: {campaign.template_rejection_reason}
                  </div>
                )}

                {!['completa', 'incompleta', 'executando'].includes(campaign.status_execucao) && (
                  <>
                  {/* Botão Marcar como Pronta */}
                  {(!campaign.status_campanha || campaign.status_campanha === 'rascunho') && 
                   (campaign.tipo_campanha === 'grupo_whatsapp' || campaign.template_status === 'aprovado') && (
                    <button
                      onClick={async () => {
                        try {
                          await campaignsAPI.update(id, { status_campanha: 'pronta' });
                          toast.success('Campanha marcada como pronta!');
                          setCampaign({ ...campaign, status_campanha: 'pronta' });
                        } catch (e) { toast.error('Erro ao atualizar'); }
                      }}
                      className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${darkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700' : 'bg-blue-500 border-blue-400 hover:bg-blue-600'} text-white`}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Marcar como Pronta
                    </button>
                  )}
                  {/* Botão voltar para rascunho */}
                  {campaign.status_campanha === 'pronta' && !campaign.status_execucao && (
                    <button
                      onClick={async () => {
                        try {
                          await campaignsAPI.update(id, { status_campanha: 'rascunho' });
                          toast.success('Campanha voltou para rascunho');
                          setCampaign({ ...campaign, status_campanha: 'rascunho' });
                        } catch (e) { toast.error('Erro ao atualizar'); }
                      }}
                      className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${darkMode ? 'bg-yellow-600 border-yellow-500 hover:bg-yellow-700' : 'bg-yellow-500 border-yellow-400 hover:bg-yellow-600'} text-white`}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Voltar p/ Rascunho
                    </button>
                  )}
                  </>
                )}

                {/* Botões de controle de execução */}
                {campaign.status_execucao === 'executando' && (
                  <>
                    <button
                      onClick={async () => {
                        try {
                          await campaignsAPI.pausar(id);
                          toast.success('Campanha pausada');
                          setCampaign({ ...campaign, status_execucao: 'pausada' });
                        } catch (e) { toast.error(e.response?.data?.message || 'Erro ao pausar'); }
                      }}
                      className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${darkMode ? 'bg-yellow-600 border-yellow-500 hover:bg-yellow-700' : 'bg-yellow-500 border-yellow-400 hover:bg-yellow-600'} text-white`}
                    >
                      <Pause className="h-4 w-4 mr-2" />
                      Pausar
                    </button>
                    <button
                      onClick={() => {
                        showConfirm({
                          title: 'Cancelar Campanha',
                          message: 'Tem certeza? Os envios já realizados não serão desfeitos, mas os pendentes não serão enviados.',
                          confirmText: 'Cancelar Campanha',
                          type: 'danger',
                          onConfirm: async () => {
                            try {
                              await campaignsAPI.cancelar(id);
                              toast.success('Campanha cancelada');
                              setCampaign({ ...campaign, status_execucao: 'cancelada' });
                            } catch (e) { toast.error(e.response?.data?.message || 'Erro ao cancelar'); }
                          }
                        });
                      }}
                      className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${darkMode ? 'bg-red-600 border-red-500 hover:bg-red-700' : 'bg-red-500 border-red-400 hover:bg-red-600'} text-white`}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancelar
                    </button>
                  </>
                )}

                {/* Botão retomar campanha pausada */}
                {(campaign.status_execucao === 'pausada' || campaign.status_execucao === 'incompleta') && (
                  <>
                    <button
                      onClick={async () => {
                        try {
                          const res = await campaignsAPI.retomar(id);
                          if (res.data?.success) {
                            toast.success('Campanha retomada');
                            setCampaign({ ...campaign, status_execucao: 'executando' });
                          } else {
                            toast.error(res.data?.message || 'Erro ao retomar');
                          }
                        } catch (e) { toast.error(e.response?.data?.message || 'Erro ao retomar'); }
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Retomar
                    </button>
                    <button
                      onClick={() => {
                        showConfirm({
                          title: 'Cancelar Campanha',
                          message: 'Tem certeza? Os envios já realizados não serão desfeitos, mas os pendentes não serão enviados.',
                          confirmText: 'Cancelar Campanha',
                          type: 'danger',
                          onConfirm: async () => {
                            try {
                              await campaignsAPI.cancelar(id);
                              toast.success('Campanha cancelada');
                              setCampaign({ ...campaign, status_execucao: 'cancelada' });
                            } catch (e) { toast.error(e.response?.data?.message || 'Erro ao cancelar'); }
                          }
                        });
                      }}
                      className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${darkMode ? 'bg-red-600 border-red-500 hover:bg-red-700' : 'bg-red-500 border-red-400 hover:bg-red-600'} text-white`}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancelar
                    </button>
                  </>
                )}

                <button
                  onClick={handleExecute}
                  disabled={executing || !campaign.status || messages.length === 0 || ['completa', 'incompleta', 'executando', 'pausada', 'cancelada'].includes(campaign.status_execucao) || (campaign.template_meta_id && campaign.template_status && campaign.template_status !== 'aprovado')}
                  className="gradient-button inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title={
                    ['completa', 'incompleta', 'executando', 'pausada', 'cancelada'].includes(campaign.status_execucao) ? 'Campanha já executada. Duplique para reenviar.' :
                      messages.length === 0 ? 'Adicione mensagens primeiro' :
                        !campaign.status ? 'Ative a campanha primeiro' :
                          (campaign.template_meta_id && campaign.template_status && campaign.template_status !== 'aprovado') ? 'Aguarde aprovação do template pelo WhatsApp' :
                            ''
                  }
                >
                  {executing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Executando...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Executar Agora
                    </>
                  )}
                </button>
                {['completa', 'incompleta', 'erro'].includes(campaign.status_execucao) && (
                  <button
                    onClick={async () => {
                      try {
                        const res = await campaignsAPI.duplicar(id);
                        if (res.data?.success) {
                          toast.success('Campanha duplicada como rascunho');
                          navigate(`/marketing/campanhas/editar/${res.data.id}`);
                        } else {
                          toast.error(res.data?.message || 'Erro ao duplicar');
                        }
                      } catch (e) { toast.error('Erro ao duplicar campanha'); }
                    }}
                    className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicar como rascunho
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards Melhorados */}
        {/* Economia com Grupos */}
        {campaign.tipo_campanha === 'grupo_whatsapp' && stats.sent > 0 && (
          (() => {
            const totalParticipantes = recipients.reduce((acc, r) => acc + (Number(r.total_participantes) || 0), 0);
            const custoPorMsg = 0.50;
            const economia = totalParticipantes * custoPorMsg;
            return (
              <div className={`rounded-lg shadow-sm border overflow-hidden ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className={`h-5 w-5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Economia com Grupos</h3>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Grupos enviados</p>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.sent}</p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pessoas alcançadas</p>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{totalParticipantes.toLocaleString('pt-BR')}</p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Custo individual</p>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>~R$ {custoPorMsg.toFixed(2)}/msg</p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Você economizou</p>
                      <p className={`text-2xl font-bold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        R$ {economia.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                  <p className={`text-xs mt-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    💡 Enviar para grupos é gratuito. Se fosse individual via Meta API, custaria ~R$ {economia.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            );
          })()
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Enviadas */}
          <div className={`rounded-lg shadow-sm border overflow-hidden ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Enviadas
                  </p>
                  <p className={`mt-2 text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {stats.sent}
                  </p>
                  {stats.sent > 0 && (
                    <p className={`mt-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      de {campaign.total_contatos || campaign.total_recipients || 0} destinatários
                    </p>
                  )}
                </div>
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-green-900/20' : 'bg-green-100'}`}>
                  <Send className={`h-6 w-6 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                </div>
              </div>
            </div>
            <div className={`px-5 py-2 ${darkMode ? 'bg-green-900/10' : 'bg-green-50'}`}>
              <div className="flex items-center text-xs">
                <TrendingUp className={`h-3 w-3 mr-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                <span className={darkMode ? 'text-green-400' : 'text-green-600'}>
                  {campaign.status_execucao === 'executando' 
                    ? `⏳ Enviando... ${stats.sent}/${campaign.total_contatos || '?'} (${campaign.total_contatos ? Math.round((stats.sent / campaign.total_contatos) * 100) : 0}%)`
                    : stats.sent > 0 ? 'Campanha executada' : 'Aguardando execução'}
                </span>
              </div>
              {campaign.status_execucao === 'executando' && campaign.total_contatos > 0 && (
                <div className={`mt-1 w-full h-1.5 rounded-full ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`}>
                  <div className="h-1.5 rounded-full bg-green-500 transition-all duration-500" style={{ width: `${Math.min(100, Math.round((stats.sent / campaign.total_contatos) * 100))}%` }} />
                </div>
              )}
            </div>
          </div>

          {/* Entregues */}
          <div className={`rounded-lg shadow-sm border overflow-hidden ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Entregues
                  </p>
                  <p className={`mt-2 text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {stats.delivered}
                  </p>
                  {stats.sent > 0 && (
                    <p className={`mt-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {Math.round((stats.delivered / stats.sent) * 100)}% de entrega
                    </p>
                  )}
                </div>
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-blue-900/20' : 'bg-blue-100'}`}>
                  <CheckCircle2 className={`h-6 w-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
              </div>
            </div>
            <div className={`px-5 py-2 ${darkMode ? 'bg-blue-900/10' : 'bg-blue-50'}`}>
              <div className="flex items-center text-xs">
                <AlertCircle className={`h-3 w-3 mr-1 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={darkMode ? 'text-blue-400' : 'text-blue-600'}>
                  Confirmação aproximada
                </span>
              </div>
            </div>
          </div>

          {/* Lidas */}
          <div className={`rounded-lg shadow-sm border overflow-hidden ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Lidas
                  </p>
                  <p className={`mt-2 text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {stats.read}
                  </p>
                  {stats.delivered > 0 && (
                    <p className={`mt-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {Math.round((stats.read / stats.delivered) * 100)}% de leitura
                    </p>
                  )}
                </div>
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-purple-900/20' : 'bg-purple-100'}`}>
                  <BarChart3 className={`h-6 w-6 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
              </div>
            </div>
            <div className={`px-5 py-2 ${darkMode ? 'bg-purple-900/10' : 'bg-purple-50'}`}>
              <div className="flex items-center text-xs">
                <AlertCircle className={`h-3 w-3 mr-1 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                <span className={darkMode ? 'text-purple-400' : 'text-purple-600'}>
                  Depende de privacidade
                </span>
              </div>
            </div>
          </div>

          {/* Destinatários */}
          <div className={`rounded-lg shadow-sm border overflow-hidden ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Destinatários
                  </p>
                  <p className={`mt-2 text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {campaign.total_contatos || campaign.total_recipients || 0}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-orange-900/20' : 'bg-orange-100'}`}>
                  <Users className={`h-6 w-6 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                </div>
              </div>
            </div>
            <div className={`px-5 py-2 ${darkMode ? 'bg-orange-900/10' : 'bg-orange-50'}`}>
              <div className="flex items-center text-xs">
                <Users className={`h-3 w-3 mr-1 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                <span className={darkMode ? 'text-orange-400' : 'text-orange-600'}>
                  {campaign.tipo_campanha === 'grupo_whatsapp' 
                    ? `${campaign.total_contatos || 0} grupos WhatsApp`
                    : campaign.grupo ? `Grupo: ${campaign.grupo.nome}` : 'Contato individual'}
                </span>
              </div>
            </div>
          </div>

          {/* Falhas */}
          {stats.failed > 0 && (
            <div className={`rounded-lg shadow-sm border overflow-hidden ${darkMode ? 'bg-zinc-800 border-red-900' : 'bg-white border-red-200'}`}>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${darkMode ? 'text-red-400' : 'text-red-500'}`}>
                      Falhas
                    </p>
                    <p className={`mt-2 text-3xl font-bold text-red-500`}>
                      {stats.failed}
                    </p>
                    {(campaign.total_contatos || 0) > 0 && (
                      <p className={`mt-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {Math.round((stats.failed / (campaign.total_contatos || stats.failed)) * 100)}% de falha
                      </p>
                    )}
                  </div>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-red-900/20' : 'bg-red-100'}`}>
                    <XCircle className={`h-6 w-6 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                  </div>
                </div>
              </div>
              <div className={`px-5 py-2 ${darkMode ? 'bg-red-900/10' : 'bg-red-50'}`}>
                <div className="flex items-center text-xs">
                  <AlertCircle className={`h-3 w-3 mr-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                  <span className={darkMode ? 'text-red-400' : 'text-red-600'}>
                    Não entregues
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Card de Custo */}
        {campaign.tipo_campanha !== 'grupo_whatsapp' && (custoReal || (custoEstimado && !['completa', 'incompleta', 'executando'].includes(campaign.status_execucao))) && (
          <div className={`rounded-lg shadow-sm border overflow-hidden ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className={`h-5 w-5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {custoReal ? 'Custo da Campanha' : 'Custo Estimado da Campanha'}
                </h3>
              </div>
              {custoReal ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Mensagens</p>
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{Number(custoReal.total_msgs) || 0}</p>
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Entregues</p>
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{Number(custoReal.total_entregues) || 0}</p>
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Falhas</p>
                    <p className={`text-sm font-medium ${Number(custoReal.total_falhas) > 0 ? 'text-red-500' : darkMode ? 'text-white' : 'text-gray-900'}`}>{Number(custoReal.total_falhas) || 0}</p>
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Gasto</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      R$ {parseFloat(custoReal.custo_total_brl || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tipo</p>
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {custoEstimado.tipo_mensagem.charAt(0).toUpperCase() + custoEstimado.tipo_mensagem.slice(1)}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Custo/msg</p>
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      R$ {custoEstimado.custo_por_mensagem_brl.toFixed(4)}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Destinatários</p>
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {custoEstimado.total_contatos.toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Estimado</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      R$ {custoEstimado.custo_total_brl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Campaign Info - Removido, informações já estão no header */}

        {/* Mensagens */}
        <div className={`rounded-lg shadow-sm border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`px-6 py-4 border-b flex items-center space-x-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <MessageSquare className={`h-5 w-5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
            <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Mensagens ({messages.length})</h3>
          </div>
          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-6 items-start">
              <div className="w-full lg:w-[35%] shrink-0 flex flex-col items-center">
                <p className={`mb-4 text-sm text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  💡 Clique nas mensagens para editar • Digite abaixo para adicionar nova
                </p>
                {/* Avisos */}
                {campaign.fluxo_id && (
                  <div className={`mb-4 w-full rounded-lg p-3 ${darkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <p className={`text-xs ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                      ⚠️ Campanha com fluxo. Apenas a 1ª mensagem é template, as seguintes vêm do fluxo.
                    </p>
                  </div>
                )}
                {!campaign.fluxo_id && campaign.template_meta_id && (
                  <div className={`mb-4 w-full rounded-lg p-3 ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                    <p className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      ℹ️ <strong>Msg 1:</strong> Template (aprovação Meta) · <strong>Msg 2+:</strong> Via fluxo após resposta
                    </p>
                  </div>
                )}
                <div className={`w-full max-w-md rounded-3xl shadow-2xl overflow-hidden ${darkMode ? 'bg-zinc-900' : 'bg-gray-100'}`}>
                  {/* Topo do celular */}
                  <div className={`px-6 py-4 ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-semibold">
                        {campaign.nome?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {campaign.nome}
                        </h3>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          online
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Área de mensagens */}
                  <div className={`p-4 space-y-3 min-h-[500px] max-h-[500px] overflow-y-auto ${darkMode ? 'bg-zinc-900' : 'bg-gray-50'}`}
                    style={{ backgroundImage: darkMode ? 'none' : 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%239C92AC\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} >
                    {campaign?.template_id && campaign?.template?.mensagem ? (
                      <div className="flex justify-end">
                        <div className="max-w-[85%] w-full">
                          <div className={`rounded-lg px-4 py-2 ${darkMode ? 'bg-green-700' : 'bg-green-500'} text-white shadow-sm`}>
                            <div className={`text-[10px] mb-1 ${darkMode ? 'text-green-300' : 'text-green-100'}`}>📋 Template: {campaign.template.nome}</div>
                            {messages[0]?.media && (messages[0].media._url || messages[0].media.nome) && (
                              <div className="mb-1">
                                {messages[0].media.type?.match(/\.(jpg|jpeg|png|gif|webp)/i) ? (
                                  <img src={messages[0].media._url || messages[0].media.nome} alt="" className="rounded-lg max-h-48 w-full object-cover" />
                                ) : messages[0].media.type?.match(/\.(mp4|mov|avi)/i) ? (
                                  <div className="relative rounded-lg overflow-hidden"><video src={messages[0].media._url || messages[0].media.nome} className="w-full max-h-52 object-cover" preload="metadata" /><div className="absolute inset-0 flex items-center justify-center bg-black/20"><div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center"><span className="ml-1 text-green-600 text-xl">▶</span></div></div></div>
                                ) : null}
                              </div>
                            )}
                            <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{
                              campaign.template.mensagem.replace(/\{\{(\w+)\}\}/g, (match, key) => {
                                if (key.toLowerCase() === 'nome') return 'João';
                                if (key.toLowerCase() === 'telefone') return '11999999999';
                                return variaveisCampanha[key] || match;
                              })
                            }</p>
                            {/* Botões do template */}
                            {campaign.template.buttons?.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-green-600/30 space-y-1">
                                {campaign.template.buttons.map((btn, idx) => (
                                  <div key={idx} className="text-center py-1.5 rounded bg-white/10 hover:bg-white/20 transition-colors cursor-default">
                                    <span className="text-sm font-medium text-white/90">{btn.text}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <MessageSquare className={`mx-auto h-12 w-12 ${darkMode ? 'text-gray-600' : 'text-gray-300'} mb-2`} />
                          <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Nenhuma mensagem
                          </p>
                        </div>
                      </div>
                    ) : (
                      messages.map((message, index) => (
                        <div key={message.id || index} className="flex justify-end group">
                          <div className="max-w-[85%] w-full">
                            {editingMessage === message.id ? (
                              <div className={`rounded-lg p-3 ${darkMode ? 'bg-green-700' : 'bg-green-500'} text-white shadow-lg mb-2`}>
                                <textarea
                                  value={editText}
                                  onChange={(e) => {
                                    setEditText(e.target.value);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleSaveMessage(message.id);
                                    }
                                    if (e.key === 'Escape') {
                                      handleCancelEdit();
                                    }
                                  }}
                                  className={`w-full bg-transparent text-white placeholder-green-200 text-sm resize-none focus:outline-none`}
                                  style={{
                                    minHeight: '60px',
                                    height: 'auto',
                                    overflow: 'hidden'
                                  }}
                                  autoFocus
                                  placeholder="Digite sua mensagem..."
                                  onInput={(e) => {
                                    e.target.style.height = 'auto';
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                  }}
                                />
                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-nomoz-orange">
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                      className="p-1 rounded hover:bg-green-600 transition-colors"
                                    >
                                      <Smile className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteMessage(message.id)}
                                      className="p-1 rounded hover:bg-red-600 transition-colors"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={handleCancelEdit}
                                      className="text-xs px-2 py-1 rounded hover:bg-green-600"
                                    >
                                      Cancelar
                                    </button>
                                    <button
                                      onClick={() => handleSaveMessage(message.id)}
                                      disabled={savingMessage || !editText.trim()}
                                      className="text-xs px-3 py-1 bg-white text-green-600 rounded font-medium hover:bg-green-50 disabled:opacity-50"
                                    >
                                      {savingMessage ? '...' : 'Salvar'}
                                    </button>
                                  </div>
                                </div>
                                {showEmojiPicker && (
                                  <div ref={emojiPickerRef} className="mt-2">
                                    <EmojiPicker
                                      onEmojiClick={onEmojiClick}
                                      theme={darkMode ? 'dark' : 'light'}
                                      width="100%"
                                      height={300}
                                    />
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div
                                className={`rounded-lg px-4 py-2 cursor-pointer transition-all ${darkMode ? 'bg-green-700 hover:bg-green-600' : 'bg-green-500 hover:bg-green-600'
                                  } text-white shadow-sm group-hover:shadow-md`}
                                onClick={() => handleEditMessage(message)}
                              >
                                {message.media && (message.media._url || message.media.nome) && (
                                  <div className="mb-1">
                                    {message.media.type?.match(/\.(jpg|jpeg|png|gif|webp)/i) ? (
                                      <img src={message.media._url || message.media.nome} alt="" className="rounded-lg max-h-48 w-full object-cover" />
                                    ) : message.media.type?.match(/\.(mp4|mov|avi)/i) ? (
                                      <div className="relative rounded-lg overflow-hidden"><video src={message.media._url || message.media.nome} className="w-full max-h-52 object-cover" preload="metadata" /><div className="absolute inset-0 flex items-center justify-center bg-black/20"><div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center"><span className="ml-1 text-green-600 text-xl">▶</span></div></div></div>
                                    ) : message.media.type?.match(/\.(mp3|ogg|wav)/i) ? (
                                      <audio src={message.media._url || message.media.nome} className="w-full mt-1" controls />
                                    ) : (
                                      <div className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? 'bg-green-600' : 'bg-green-400'}`}>
                                        <span>📎</span><span className="text-xs truncate">{message.media.nome?.split('/').pop() || 'Documento'}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                                  {message.text?.replace(/\{\{(\w+)\}\}/g, (match, key) => {
                                    if (key.toLowerCase() === 'nome') return 'João';
                                    if (key.toLowerCase() === 'telefone') return '11999999999';
                                    return variaveisCampanha[key] || match;
                                  })}
                                </p>
                                {message.media_id && !message.media && (
                                  <div className={`mt-2 text-xs ${darkMode ? 'text-green-200' : 'text-green-100'} flex items-center`}>
                                    <span>📎 Mídia anexada</span>
                                  </div>
                                )}
                              </div>
                            )}
                            {editingMessage !== message.id && (
                              <div className="flex items-center justify-end mt-1 space-x-1">
                                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <CheckCircle2 className={`h-3 w-3 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {/* Rodapé do celular - Campo de nova mensagem */}
                  <div className={`px-4 py-3 ${darkMode ? 'bg-zinc-800' : 'bg-white'} border-t ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
                    {/* Preview de mídia */}
                    {mediaPreview && (
                      <div className="mb-2 relative inline-block">
                        <img src={mediaPreview} alt="Preview" className="max-h-32 rounded-lg" />
                        <button
                          onClick={handleRemoveMedia}
                          className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    {mediaFile && !mediaPreview && (
                      <div className={`mb-2 flex items-center gap-2 px-3 py-2 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-gray-100'}`}>
                        <Paperclip className="h-4 w-4" />
                        <span className="text-sm flex-1 truncate">{mediaFile.name}</span>
                        <button onClick={handleRemoveMedia} className="text-red-500 hover:text-red-600">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    {/* Variáveis disponíveis */}
                    <div className={`flex items-center gap-2 mb-2 px-1`}>
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Variáveis:</span>
                      {[{ label: 'Nome', value: '{{nome}}' }, { label: 'Telefone', value: '{{telefone}}' }].map(v => (
                        <button
                          key={v.value}
                          type="button"
                          onClick={() => setNewMessageText(prev => prev + v.value)}
                          className={`px-2 py-0.5 rounded text-xs font-mono transition-colors ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-purple-400' : 'bg-gray-100 hover:bg-gray-200 text-purple-600'
                            }`}
                        >
                          {v.value}
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      {showNewEmojiPicker && (
                        <div ref={newEmojiPickerRef} className="absolute bottom-full mb-2 left-0 z-50">
                          <EmojiPicker
                            onEmojiClick={onNewEmojiClick}
                            theme={darkMode ? 'dark' : 'light'}
                            width={320}
                            height={400}
                            searchPlaceHolder="Buscar emoji..."
                            previewConfig={{ showPreview: false }}
                          />
                        </div>
                      )}
                      <div className={`flex items-center space-x-2 px-3 py-2 rounded-full ${darkMode ? 'bg-zinc-700' : 'bg-gray-100'}`}>
                        <input
                          type="file"
                          id="media-upload"
                          accept="image/*,video/*,audio/*"
                          onChange={handleMediaSelect}
                          className="hidden"
                        />
                        <label
                          htmlFor="media-upload"
                          className={`p-1 rounded-full transition-colors cursor-pointer ${darkMode ? 'hover:bg-zinc-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                            }`}
                        >
                          <Paperclip className="h-5 w-5" />
                        </label>
                        <button
                          onClick={() => setShowNewEmojiPicker(!showNewEmojiPicker)}
                          className={`p-1 rounded-full transition-colors ${darkMode ? 'hover:bg-zinc-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                            }`}
                        >
                          <Smile className="h-5 w-5" />
                        </button>
                        <textarea
                          value={newMessageText}
                          onChange={(e) => {
                            setNewMessageText(e.target.value);
                          }}
                          onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleAddMessage();
                            }
                          }}
                          placeholder="Digite uma mensagem..."
                          className={`flex-1 bg-transparent text-sm outline-none resize-none ${darkMode ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'
                            }`}
                          style={{ minHeight: '24px', maxHeight: '120px', overflow: 'hidden' }}
                          rows={1}
                        />
                        {(newMessageText.trim() || mediaFile) && (
                          <button
                            onClick={handleAddMessage}
                            disabled={addingMessage || uploadingMedia}
                            className="p-1 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors disabled:opacity-50"
                          >
                            {(addingMessage || uploadingMedia) ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                              <Send className="h-5 w-5" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="lg:flex-1">
                {(() => {
                  const texto = campaign?.template?.mensagem || messages?.[0]?.text || '';
                  const allVars = [...new Set((texto.match(/\{\{(\w+)\}\}/g) || []).map(v => v.replace(/\{\{|\}\}/g, '')))];
                  const autoVars = allVars.filter(v => ['nome', 'telefone'].includes(v.toLowerCase()));

                  // Variáveis declaradas pelo fluxo vinculado. Ficam na MESMA seção
                  // para o usuário preencher tudo de uma vez — o fluxo lê estes
                  // mesmos valores de `variaveis_campanha` na hora de responder.
                  const varsFluxo = (campaign.fluxo_id && Array.isArray(fluxoData?.variaveis))
                    ? fluxoData.variaveis
                    : [];
                  const metaVarFluxo = {};
                  for (const v of varsFluxo) {
                    const chave = String(v?.chave || '').toLowerCase();
                    if (chave) metaVarFluxo[chave] = v;
                  }

                  // Uniao (template ∪ fluxo), sem 'nome'/'telefone' (automáticas) e
                  // sem duplicar quando template e fluxo usam a mesma chave.
                  const customVars = [...new Set([
                    ...allVars.filter(v => !['nome', 'telefone'].includes(v.toLowerCase())),
                    ...Object.keys(metaVarFluxo),
                  ])];

                  // Onde cada variável aparece no fluxo: nome da etapa + trecho da
                  // mensagem em volta do {{placeholder}}. É o que responde "esse
                  // valor vai sair aonde?" na hora de preencher.
                  const etapasFluxo = (campaign.fluxo_id && Array.isArray(fluxoData?.etapas)) ? fluxoData.etapas : [];
                  const usosDaVariavel = (chave) => {
                    const re = new RegExp(`\\{\\{\\s*${chave}\\s*\\}\\}`, 'i');
                    const usos = [];
                    for (const et of etapasFluxo) {
                      const textos = [
                        et.mensagem_automatica,
                        et.mensagem_fallback,
                        ...((et.opcoes_menu || []).map(o => o?.texto)),
                      ].filter(Boolean);
                      const alvo = textos.find(t => re.test(t));
                      if (alvo) {
                        // Recorta ~30 chars ao redor do placeholder para dar contexto.
                        const idx = alvo.search(re);
                        const ini = Math.max(0, idx - 25);
                        const trecho = (ini > 0 ? '…' : '') + alvo.slice(ini, idx + chave.length + 30).trim() + '…';
                        usos.push({ etapa: et.nome || et.tipo_etapa, trecho });
                      }
                    }
                    return usos;
                  };

                  if (allVars.length === 0 && customVars.length === 0) return null;
                  return (
                    <div className={`mt-6 rounded-2xl p-5 ${darkMode ? 'bg-zinc-800/40' : 'bg-gray-50/80'}`}>
                      <div className="flex items-center gap-2 mb-4">
                        <Settings className={`h-4 w-4 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                        <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Variáveis da campanha</h4>
                      </div>

                      {autoVars.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {autoVars.map(v => (
                            <span key={v} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${darkMode ? 'bg-zinc-700 text-green-400' : 'bg-white text-green-700 shadow-sm'}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                              {`{{${v}}}`} — automática
                            </span>
                          ))}
                        </div>
                      )}

                      {customVars.length > 0 && (
                        <>
                          <p className={`text-xs mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Valores iguais para todos os contatos da campanha:
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {customVars.map(key => {
                              const decl = metaVarFluxo[key];
                              const usos = usosDaVariavel(key);
                              return (
                              <div key={key}>
                                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                  {decl?.label || key}
                                  {decl?.obrigatoria && <span className="text-red-500"> *</span>}
                                  {decl && (
                                    <span className={`ml-2 text-[11px] font-normal ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                                      {`{{${key}}}`} — do fluxo
                                    </span>
                                  )}
                                </label>
                                <textarea
                                  value={variaveisCampanha[key] || ''}
                                  onChange={e => {
                                    setVariaveisCampanha({ ...variaveisCampanha, [key]: e.target.value });
                                    e.target.style.height = 'auto';
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                  }}
                                  placeholder={decl?.padrao ? `Padrão: ${decl.padrao}` : `Digite o valor de ${key}...`}
                                  rows={1}
                                  className={`w-full rounded-xl px-4 py-3 text-sm resize-none overflow-hidden focus:outline-none transition-all ${darkMode ? 'bg-zinc-700/80 text-white placeholder-gray-500 focus:bg-zinc-600 focus:ring-1 focus:ring-purple-500/30' : 'bg-white text-gray-900 placeholder-gray-400 shadow-sm focus:shadow-md focus:ring-1 focus:ring-purple-500/30'}`}
                                />
                                {/* Onde a variável aparece no fluxo — só quando
                                    houver uso, como referência útil. A variável
                                    é válida por estar declarada no fluxo; não há
                                    aviso de "não usada". */}
                                {decl && usos.length > 0 && (
                                  <div className="mt-1.5 space-y-1">
                                    {usos.map((u, i) => (
                                      <p key={i} className={`text-[11px] leading-snug ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        📍 <span className="font-medium">{u.etapa}</span>
                                        <span className="opacity-80"> — “{u.trecho}”</span>
                                      </p>
                                    ))}
                                  </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                          <div className="mt-4 flex justify-end">
                            <button
                              onClick={async () => {
                                try {
                                  await campaignsAPI.update(campaign.id, { variaveis_campanha: variaveisCampanha });
                                  toast.success('Variáveis salvas');
                                } catch { toast.error('Erro ao salvar'); }
                              }}
                              className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-xl font-semibold transition-all hover:shadow-lg hover:shadow-purple-500/20"
                            >
                              Salvar variáveis
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* Fluxo vinculado — fluxograma visual.
                    Mostra sempre que há fluxo vinculado, mesmo sem botões no
                    template: fluxo pode ser acionado por texto livre, e o
                    operador precisa ver que a campanha tem um fluxo. */}
                {campaign.fluxo_id && fluxoData && (() => {
                  const etapas = fluxoData.etapas || [];
                  const menuEtapa = etapas.find(e => e.tipo_etapa === 'menu');
                  const opcoesFluxo = menuEtapa?.opcoes_menu || [];
                  const botoesTemplate = campaign.template?.buttons || [];
                  
                  const mismatch = botoesTemplate.length > 0 && opcoesFluxo.length > 0 && 
                    !botoesTemplate.every(btn => opcoesFluxo.some(op => 
                      op.texto?.toLowerCase().trim() === btn.text?.toLowerCase().trim() ||
                      op.opcao?.toLowerCase().trim() === btn.text?.toLowerCase().trim()
                    ));

                  return (
                    <div className={`mt-6 rounded-2xl p-5 ${darkMode ? 'bg-zinc-800/40' : 'bg-gray-50/80'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span>🤖</span>
                          <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Fluxo: {fluxoData.nome}</h4>
                        </div>
                        {mismatch && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-medium rounded-lg">⚠️ Mismatch</span>}
                      </div>

                      {mismatch && (
                        <div className={`mb-3 p-2.5 rounded-lg border text-xs ${darkMode ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                          ⚠️ Botões do template não correspondem às opções do fluxo — contato pode receber "Opção inválida".
                        </div>
                      )}

                      <FluxoMiniMap etapas={etapas} botoesTemplate={botoesTemplate} darkMode={darkMode} variaveis={fluxoData?.variaveis || []} valores={variaveisCampanha} />
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>


        {/* Destinatários */}
      <div className={`rounded-lg shadow-sm border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className={`px-6 py-4 border-b flex items-center space-x-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <Users className={`h-5 w-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Destinatários</h3>
        </div>
        <div className="p-6">
          <div>
            {/* Lista paginada de envios */}
            {(
              <div className="mt-6">
                {/* Busca e Filtros */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <input
                    type="text"
                    placeholder="Buscar por nome ou telefone..."
                    value={recipientsSearch}
                    onChange={e => {
                      setRecipientsSearch(e.target.value);
                      clearTimeout(window._recipientSearchTimer);
                      window._recipientSearchTimer = setTimeout(() => loadRecipients(1, e.target.value, recipientsStatus), 400);
                    }}
                    className={`flex-1 min-w-[180px] px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
                  />
                  {/* Filtros de status */}
                  <div className="flex gap-1 flex-wrap">
                    {[
                      { value: '', label: 'Todos' },
                      { value: 'enviada', label: '✓ Enviada' },
                      { value: 'entregue', label: '✓✓ Entregue' },
                      { value: 'lida', label: '👁 Lida' },
                      { value: 'falhou', label: '✗ Falhou' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setRecipientsStatus(opt.value);
                          loadRecipients(1, recipientsSearch, opt.value);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          recipientsStatus === opt.value
                            ? opt.value === 'falhou' ? 'bg-red-500 text-white'
                              : opt.value === 'lida' ? 'bg-purple-500 text-white'
                              : opt.value === 'entregue' ? 'bg-green-500 text-white'
                              : opt.value === 'enviada' ? 'bg-blue-500 text-white'
                              : darkMode ? 'bg-zinc-500 text-white' : 'bg-gray-700 text-white'
                            : darkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <span className={`text-sm whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {recipientsTotal.toLocaleString('pt-BR')} registros
                  </span>
                  <button
                    onClick={handleExportCsv}
                    disabled={exportingCsv}
                    title="Exportar relatório em CSV (respeita busca e filtro de status)"
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                    {exportingCsv ? 'Exportando...' : 'Exportar CSV'}
                  </button>
                </div>

                {/* Tabela */}
                {recipientsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  </div>
                ) : recipients.length > 0 ? (
                  <>
                    <div className={`rounded-lg border overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <table className="w-full text-sm">
                        <thead className={darkMode ? 'bg-zinc-700' : 'bg-gray-50'}>
                          <tr>
                            <th className={`px-4 py-3 text-left font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{campaign?.tipo_campanha === 'grupo_whatsapp' ? 'Grupo' : 'Nome'}</th>
                            {campaign?.tipo_campanha === 'grupo_whatsapp' && (
                              <th className={`px-4 py-3 text-left font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Participantes</th>
                            )}
                            <th className={`px-4 py-3 text-left font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{campaign?.tipo_campanha === 'grupo_whatsapp' ? 'ID' : 'Telefone'}</th>
                            <th className={`px-4 py-3 text-left font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Status</th>
                            <th className={`px-4 py-3 text-left font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Data</th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                          {recipients.map((r, i) => (
                            <tr key={r.id || i} className={darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-50'}>
                              <td className={`px-4 py-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{r.nome || r.group_name || '—'}</td>
                              {campaign?.tipo_campanha === 'grupo_whatsapp' && (
                                <td className={`px-4 py-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{r.total_participantes || '—'}</td>
                              )}
                              <td className={`px-4 py-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{r.telefone}</td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    ['lida','read'].includes(r.status) ? 'bg-purple-100 text-purple-700' :
                                    ['entregue','delivered'].includes(r.status) ? 'bg-green-100 text-green-700' :
                                    ['enviada'].includes(r.status) ? 'bg-blue-100 text-blue-700' :
                                    ['erro','failed'].includes(r.status) ? 'bg-red-100 text-red-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}
                                  title={['erro','failed'].includes(r.status) && r.motivo_rotulo
                                    ? `${r.motivo_rotulo}\n${r.motivo_descricao || ''}`
                                    : undefined}
                                >
                                  {['lida','read'].includes(r.status) ? '👁 Lida' :
                                   ['entregue','delivered'].includes(r.status) ? '✓✓ Entregue' :
                                   ['enviada'].includes(r.status) ? '✓ Enviada' :
                                   ['erro','failed'].includes(r.status) ? '✗ Falhou' : r.status}
                                </span>
                              </td>
                              <td className={`px-4 py-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {r.create_at ? new Date(r.create_at).toLocaleString('pt-BR') : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Paginação */}
                    {recipientsPages > 1 && (
                      <div className="flex items-center justify-between mt-4">
                        <button
                          onClick={() => loadRecipients(recipientsPage - 1, recipientsSearch, recipientsStatus)}
                          disabled={recipientsPage <= 1}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                        >
                          ← Anterior
                        </button>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Página {recipientsPage} de {recipientsPages}
                        </span>
                        <button
                          onClick={() => loadRecipients(recipientsPage + 1, recipientsSearch, recipientsStatus)}
                          disabled={recipientsPage >= recipientsPages}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                        >
                          Próxima →
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className={`text-sm text-center py-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Nenhum registro encontrado
                  </p>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Histórico */}
      <div className={`rounded-lg shadow-sm border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className={`px-6 py-4 border-b flex items-center space-x-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <BarChart3 className={`h-5 w-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
          <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Histórico de Execuções</h3>
        </div>
        <div className="p-6">
          <div>
            <div className="flex justify-between items-center mb-6">
              <div></div>
              <div className="flex flex-wrap space-x-2">
                <button
                  onClick={loadCampaignDetails}
                  className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${darkMode
                    ? 'border-gray-600 text-gray-300 bg-zinc-700 hover:bg-zinc-600'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                >
                  Atualizar Histórico
                </button>
                <button
                  onClick={() => {
                    showConfirm({
                      title: 'Limpar Histórico',
                      message: 'Tem certeza que deseja limpar todo o histórico desta campanha? Esta ação não pode ser desfeita.',
                      confirmText: 'Limpar',
                      type: 'danger',
                      onConfirm: async () => {
                        try {
                          await campaignsAPI.clearHistory(id);
                          toast.success('Histórico limpo com sucesso!');
                          loadCampaignDetails();
                        } catch (error) {
                          toast.error('Erro ao limpar histórico');
                        }
                      }
                    });
                  }}
                  className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${darkMode
                    ? 'border-red-600 text-red-400 bg-zinc-700 hover:bg-red-900/20'
                    : 'border-red-300 text-red-700 bg-white hover:bg-red-50'
                    }`}
                >
                  Limpar Histórico
                </button>
              </div>
            </div>

            {/* Fila do pool — só aparece quando a campanha foi materializada */}
            {fila && (
              <div className={`rounded-lg border p-4 mb-6 ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    ⚡ Fila de disparo (pool)
                  </h3>
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {fila.total} item(ns)
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                  {[
                    { chave: 'pendente', rotulo: 'Na fila', cor: darkMode ? 'text-gray-200' : 'text-gray-800' },
                    { chave: 'em_voo', rotulo: 'Enviando', cor: 'text-blue-500' },
                    { chave: 'enviado', rotulo: 'Enviados', cor: 'text-green-500' },
                    { chave: 'falha_retentavel', rotulo: 'Vão retentar', cor: 'text-amber-500' },
                    { chave: 'falha_permanente', rotulo: 'Encerrados', cor: darkMode ? 'text-gray-400' : 'text-gray-500' },
                    { chave: 'suspeito', rotulo: 'Suspeitos', cor: 'text-red-500' },
                    { chave: 'cancelado', rotulo: 'Cancelados', cor: darkMode ? 'text-gray-500' : 'text-gray-400' },
                  ].map(({ chave, rotulo, cor }) => (
                    <div key={chave} className={`rounded-lg p-3 text-center ${darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
                      <div className={`text-xl font-bold ${cor}`}>{fila.por_status?.[chave] || 0}</div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{rotulo}</div>
                    </div>
                  ))}
                </div>

                {fila.suspeitos > 0 && (
                  <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                    <p className="text-sm text-red-600 dark:text-red-400">
                      <strong>{fila.suspeitos} item(ns) suspeito(s).</strong> O envio foi
                      interrompido no meio e não há como saber se a mensagem chegou. Por isso
                      não são reenviados automaticamente — reenviar assume o risco de a pessoa
                      receber duas vezes.
                    </p>
                    <button
                      onClick={() => {
                        showConfirm({
                          title: 'Reenviar itens suspeitos',
                          message: `${fila.suspeitos} item(ns) voltam para a fila. Se a mensagem já tinha saído, a pessoa vai receber duas vezes.`,
                          confirmText: 'Reenviar',
                          type: 'danger',
                          onConfirm: async () => {
                            setReenfileirando(true);
                            try {
                              const { data } = await campaignsAPI.reenfileirarSuspeitos(id);
                              toast.success(`${data.reenfileirados} item(ns) devolvidos à fila`);
                              const { data: nova } = await campaignsAPI.getFila(id);
                              setFila(nova?.usando_pool ? nova : null);
                            } catch {
                              toast.error('Erro ao reenfileirar');
                            } finally {
                              setReenfileirando(false);
                            }
                          },
                        });
                      }}
                      disabled={reenfileirando}
                      className="mt-3 px-4 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-50"
                    >
                      {reenfileirando ? 'Reenviando…' : 'Devolver à fila'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Estatísticas Resumidas */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className={`rounded-lg p-4 text-center ${darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
                <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {stats.sent}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Enviadas
                </div>
              </div>
              <div className={`rounded-lg p-4 text-center ${darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
                <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {stats.delivered}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Entregues*
                </div>
              </div>
              <div className={`rounded-lg p-4 text-center ${darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
                <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {stats.read}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Lidas*
                </div>
              </div>
            </div>

            {/* Aviso sobre privacidade */}
            <div className={`rounded-lg p-3 mb-6 ${darkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'
              } border`}>
              <div className="flex items-start space-x-2">
                <div className={`mt-0.5 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>⚠️</div>
                <div className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                  <strong>Importante:</strong> Os números de entrega e leitura são aproximados e dependem das configurações de privacidade dos destinatários. Usuários podem desabilitar confirmações de leitura ou entrega em suas configurações do WhatsApp.
                </div>
              </div>
            </div>

            {/* Taxa de Sucesso */}
            {stats.sent > 0 && (
              <div className={`mt-6 rounded-lg p-4 ${darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
                <h5 className={`text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  📊 Taxa de Sucesso (Aproximada)
                </h5>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Taxa de Entrega:</span>
                    <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      ~{stats.sent > 0 ? Math.round((stats.delivered / stats.sent) * 100) : 0}%
                    </span>
                  </div>
                  <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${stats.sent > 0 ? (stats.delivered / stats.sent) * 100 : 0}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Taxa de Leitura:</span>
                    <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      ~{stats.delivered > 0 ? Math.round((stats.read / stats.delivered) * 100) : 0}%
                    </span>
                  </div>
                  <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: `${stats.delivered > 0 ? (stats.read / stats.delivered) * 100 : 0}%` }}
                    ></div>
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-2`}>
                    * Valores aproximados baseados nas confirmações recebidas
                  </div>
                </div>
              </div>)}

            {/* Taxa de Conversão do Fluxo */}
            {conversao && conversao.total_enviados > 0 && (
              <div className={`mt-6 rounded-lg p-4 ${darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
                <h5 className={`text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  🎯 Taxa de Conversão
                </h5>
                <div className="space-y-3">
                  {/* Barra geral */}
                  <div className="flex justify-between text-sm">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Responderam:</span>
                    <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {conversao.total_responderam}/{conversao.total_enviados} ({conversao.taxa_resposta}%)
                    </span>
                  </div>
                  <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div className="bg-nomoz-orange h-2 rounded-full" style={{ width: `${conversao.taxa_resposta}%` }}></div>
                  </div>

                  {/* Breakdown por opção */}
                  {conversao.opcoes?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {conversao.opcoes.map((opcao, i) => (
                        <div key={i} className={`flex items-center justify-between p-2 rounded ${darkMode ? 'bg-zinc-600' : 'bg-white border border-gray-200'}`}>
                          <div className="flex items-center gap-2">
                            <span className={`text-lg ${i === 0 ? '✅' : '❌'}`}>{i === 0 ? '✅' : '❌'}</span>
                            <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{opcao.texto}</span>
                          </div>
                          <div className="text-right">
                            <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{opcao.quantidade}</span>
                            <span className={`text-xs ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>({opcao.percentual}%)</span>
                          </div>
                        </div>
                      ))}
                      {conversao.sem_resposta > 0 && (
                        <div className={`flex items-center justify-between p-2 rounded ${darkMode ? 'bg-zinc-600' : 'bg-white border border-gray-200'}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">⏳</span>
                            <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Sem resposta</span>
                          </div>
                          <div className="text-right">
                            <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{conversao.sem_resposta}</span>
                            <span className={`text-xs ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>({Math.round((conversao.sem_resposta / conversao.total_enviados) * 100)}%)</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Modal de confirmação de submissão */}
            {showSubmitModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className={`rounded-lg shadow-xl max-w-md w-full ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
                  <div className="p-6">
                    <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Submeter Template para Aprovação
                    </h3>

                    {messages.length > 1 ? (
                      <div className="space-y-3 mb-6">
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
                          <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                            ✅ Mensagem 1
                          </p>
                          <p className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                            Será enviada como template (precisa aprovação do WhatsApp)
                          </p>
                        </div>

                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                          <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                            ✅ Mensagens 2-{messages.length}
                          </p>
                          <p className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                            Serão enviadas automaticamente via fluxo após cliente responder
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className={`text-sm mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Sua mensagem será submetida para aprovação do WhatsApp.
                      </p>
                    )}

                    <div className="mb-4">
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Categoria do Template
                      </label>
                      <select
                        value={templateCategoria}
                        onChange={(e) => setTemplateCategoria(e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      >
                        <option value="MARKETING">📢 Marketing — promoções, ofertas, novidades</option>
                        <option value="UTILITY">🔧 Utility — confirmações, atualizações, alertas</option>
                      </select>
                    </div>

                    <p className={`text-xs mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      ⏱️ A aprovação pode levar de 15 minutos até 24 horas.
                    </p>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowSubmitModal(false)}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium ${darkMode
                          ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                          }`}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={confirmarSubmissao}
                        className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                      >
                        Confirmar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de confirmação de execução */}
            {showExecuteModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className={`rounded-lg shadow-xl max-w-md w-full ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <Send className={`w-6 h-6 mr-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Executar Campanha
                      </h3>
                    </div>

                    <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Você está prestes a executar esta campanha com <strong>{messages.length}</strong> mensagem(s).
                    </p>

                    <div className={`p-3 rounded-lg mb-6 ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                      <p className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                        ℹ️ As mensagens serão enviadas para todos os contatos do grupo selecionado.
                      </p>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowExecuteModal(false)}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium ${darkMode
                          ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                          }`}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={confirmarExecucao}
                        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Executar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de confirmação de exclusão */}
            {showDeleteModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className={`rounded-lg shadow-xl max-w-md w-full ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
                  <div className="p-6">
                    <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Excluir Mensagem
                    </h3>

                    <p className={`text-sm mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Tem certeza que deseja excluir esta mensagem? Esta ação não pode ser desfeita.
                    </p>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setShowDeleteModal(false);
                          setMessageToDelete(null);
                        }}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium ${darkMode
                          ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                          }`}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={confirmDeleteMessage}
                        className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </Layout>
  );
};

export default CampaignDetails;